import React from 'react';
import { Card, Badge, Button } from '@/components/ui';
import { Settings, Play, Pause, Edit2, Zap, ArrowRight, Activity } from 'lucide-react';
import type { AutomationRule } from '../types/automation.types';

interface AutomationRuleCardProps {
  rule: AutomationRule;
  onToggle: (id: string, isActive: boolean) => void;
  onRunNow: (id: string) => void;
  onEdit?: (rule: AutomationRule) => void;
  triggerLabels: Record<string, string>;
  actionLabels: Record<string, string>;
}

export const AutomationRuleCard: React.FC<AutomationRuleCardProps> = ({
  rule,
  onToggle,
  onRunNow,
  onEdit,
  triggerLabels,
  actionLabels,
}) => {
  return (
    <Card className={`group relative border-none shadow-sm hover:shadow-xl transition-all duration-500 bg-white rounded-[32px] overflow-hidden p-0 ring-1 ring-slate-100 ${!rule.is_active ? 'opacity-75 grayscale-[0.5]' : ''}`}>
      <div className="p-6">
        <div className="flex justify-between items-start mb-6">
          <div className={`p-3 rounded-2xl ${rule.is_active ? 'bg-blue-50 text-blue-600' : 'bg-slate-50 text-slate-400'} transition-colors duration-500`}>
            <Settings className="w-6 h-6" />
          </div>
          <Badge className={`${rule.is_active ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500'} border-none font-black text-[9px]  tracking-widest px-3 py-1.5 rounded-full shadow-sm`}>
            {rule.is_active ? 'Active' : 'Paused'}
          </Badge>
        </div>

        <h3 className="font-black text-slate-900 mb-2 leading-tight group-hover:text-blue-600 transition-colors tracking-tight text-lg">
          {rule.name}
        </h3>
        <p className="text-xs text-slate-400 mb-6 font-medium leading-relaxed line-clamp-2">
          {rule.description || 'Hệ thống tự động hóa vận hành thông minh.'}
        </p>

        <div className="space-y-4 mb-8 bg-slate-50/50 p-4 rounded-[24px] border border-slate-100/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-white flex items-center justify-center shadow-sm text-blue-500">
                <Zap className="w-4 h-4" />
              </div>
              <div className="flex flex-col">
                <span className="text-[9px] text-slate-400 font-black  tracking-widest">Trigger</span>
                <span className="text-[11px] text-slate-700 font-black">{triggerLabels[rule.trigger_type] || rule.trigger_type}</span>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-slate-200" />
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-white flex items-center justify-center shadow-sm text-emerald-500">
                <Activity className="w-4 h-4" />
              </div>
              <div className="flex flex-col">
                <span className="text-[9px] text-slate-400 font-black  tracking-widest text-right">Action</span>
                <span className="text-[11px] text-slate-700 font-black text-right">{actionLabels[rule.action_type] || rule.action_type}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between pt-5 border-t border-slate-50">
          <div className="flex flex-col">
            <span className="text-[9px] text-slate-400 font-black  tracking-widest">Total Runs</span>
            <span className="text-sm font-black text-slate-900">{rule.execution_count || 0}</span>
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-10 h-10 p-0 rounded-2xl hover:bg-blue-50 hover:text-blue-600 transition-all active:scale-95 shadow-sm bg-white border border-slate-100"
              onClick={() => onRunNow(rule.id)}
              title="Run Now"
            >
              <Play className="w-4 h-4 fill-current" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={`w-10 h-10 p-0 rounded-2xl transition-all active:scale-95 shadow-sm bg-white border border-slate-100 ${rule.is_active ? 'hover:bg-amber-50 hover:text-amber-600' : 'hover:bg-emerald-50 hover:text-emerald-600'}`}
              onClick={() => onToggle(rule.id, !rule.is_active)}
              title={rule.is_active ? 'Pause' : 'Resume'}
            >
              {rule.is_active ? <Pause className="w-4 h-4 fill-current" /> : <Play className="w-4 h-4 fill-current" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="w-10 h-10 p-0 rounded-2xl hover:bg-slate-100 transition-all active:scale-95 shadow-sm bg-white border border-slate-100"
              onClick={() => onEdit?.(rule)}
            >
              <Edit2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};
